/**
 * visualConfig — the number row 1–7 as base-visual selectors.
 *
 * Where letters are effects (post-processing that layers on top) and dials are
 * continuous master controls, these pick the *base field* the whole scene is
 * built from. Selection is exclusive: pressing a visual key writes its index to
 * `visual.select` on the bus, the shader mirrors that into `uVisual`, and the
 * `sampleBase()` dispatcher switches which field function is sampled. Every
 * letter effect, dial, combo and the tempo pulse keep working on top of any base.
 *
 * Same declarative spirit as effectConfig: adding a base visual means appending
 * here + one field function in shaders.ts, nothing in the input path.
 */

export interface VisualDef {
  /** Normalized digit key ('1'..'7'). */
  readonly key: string;
  /** Exclusive selection index, written to the bus and read as `uVisual`. */
  readonly index: number;
  /** Stable id (matches the field function's intent in shaders.ts). */
  readonly id: string;
  /** Human-facing label for the on-screen selector cell. */
  readonly label: string;
  /** What the +/- modifier tunes on this visual (shown above the −/+ keys). */
  readonly modLabel: string;
}

/** paramBus key holding the active base-visual index. */
export const VISUAL_PARAM = 'visual.select';

export const visualConfig: readonly VisualDef[] = [
  { key: '1', index: 0, id: 'oscilloscope', label: 'Oscilloscope', modLabel: 'Frequency' },
  { key: '2', index: 1, id: 'tunnel', label: 'Tunnel', modLabel: 'Speed' },
  { key: '3', index: 2, id: 'oil', label: 'Oil', modLabel: 'Swirl' },
  { key: '4', index: 3, id: 'starfield', label: 'Starfield', modLabel: 'Density' },
  { key: '5', index: 4, id: 'metaballs', label: 'Lava Lamp', modLabel: 'Blobs' },
  { key: '6', index: 5, id: 'voronoi', label: 'Voronoi', modLabel: 'Cells' },
  { key: '7', index: 6, id: 'curlFlow', label: 'Curl Flow', modLabel: 'Flow' },
] as const;

/** Fast key -> definition lookup for the input controller. */
export const visualByKey: ReadonlyMap<string, VisualDef> = new Map(
  visualConfig.map((v) => [v.key, v]),
);

/**
 * paramBus key for a visual's +/- modifier value (0..1, 0.5 neutral). Kept in its
 * own `vmod.` namespace rather than the effect `mod.` one so a visual id can never
 * collide with an effect id (e.g. the `voronoi` visual vs. the `crystallize`
 * effect); the shader reads it as `uVMod<Id>` and each field function maps it to a
 * fun parameter.
 */
export function visualModParam(id: string): string {
  return `vmod.${id}`;
}
